import { TextField } from '@material-ui/core'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import SendIcon from '@material-ui/icons/Send'
import Alert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import { validate } from 'jsonschema'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import NumberFormat from 'react-number-format'
import { Link, useHistory } from 'react-router-dom'
import IenApi from '../../api/IenApi'
import { useFormUploadConfig, useFormUploadState, withFormUpload } from '../../context/FormUploadContext'
import Loading from '../../customs/Loading/Loading'
import { toastError, toastSuccess } from '../../customs/Toast/Toast'
import { EKOM_LINKS } from '../../EKOM_LINKS'
import { useFetch } from '../../hooks/useFetch'
import Error from '../../parts/Error'
import ModalConfirm from '../../parts/ModalConfirm'
import { iSubmitAttach, iSubmitExtSys, iSubmitFormData, iSubmitResponse } from '../../types/submit.types'
import { cipherString } from '../../utils/cipher'
import { errorIenProcesThrow, errorIenProcesToast } from '../../utils/errorIenProces'
import { generatePdf } from './generatePdf'
import { proccesValidErr, SCHEMA_KEYS } from './schemaValidate'
import { useStyles } from './styles'
import UploadBox from './UploadBox/UploadBox'

interface iNewMsg {
  extSystemId: string
  extObjectId: string
  typeCode: string
  isSubms?: boolean
  contractSlug?: string
}

const NewMsg = ({ extSystemId, extObjectId, typeCode, isSubms, contractSlug }: iNewMsg) => {
  const classes = useStyles()

  const contractLink = contractSlug ?
    `${EKOM_LINKS.contractDetail}/${contractSlug}` :
    `${EKOM_LINKS.contractVirtual}?${EKOM_LINKS.queryExtSys}=${extSystemId}&${EKOM_LINKS.queryExtId}=${extObjectId}`

  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitResponse, setSubmitResponse] = useState<iSubmitResponse>()
  const [trySend, setTrySend] = useState(false)
  const [openModalConfirm, setOpenModalConfirm] = useState(false)

  const { updateConfig } = useFormUploadConfig()
  const { isUploading, filesUploaded } = useFormUploadState(SCHEMA_KEYS.PRILOHY)

  const [formData, setFormData] = useState<{ text?: string, predmet?: string, cisloJednaci?: string }>({})
  const [formDataCrits, setFormDataCrits] = useState<{ [key: string]: string | number }>({})

  const setFormDataVal = (newData: Partial<typeof formData>) => {
    setFormData(data => ({ ...data, ...newData }))
  }

  const history = useHistory()
  const queryParameters = new URLSearchParams(window.location.search)
  const responseId = queryParameters.get(EKOM_LINKS.queryResponseId) || undefined
  // TODO mozna predat id a typ - uri do ekomunikace
  const responseSubject = queryParameters.get(EKOM_LINKS.queryResponseSuject) || undefined

  useEffect(() => {
    if (responseSubject) {
      setFormDataVal({ predmet: `Re: ${responseSubject}` })
      queryParameters.delete(EKOM_LINKS.queryResponseSuject)
      history.replace({
        search: queryParameters.toString(),
      })
    }
  }, [responseSubject])

  const {
    data,
    loading,
    error
  } = useFetch(() => IenApi.getContractSubmsInfoTypeExtSys(extSystemId, extObjectId, typeCode, responseId)
    .then(data => {
      updateConfig({
        extSystemId: extSystemId,
        cipherCert: data.sifrovaciCertifikat,
      })
      return data
    })
    .catch(errorIenProcesThrow)
  )

  if (error) {
    return <Error>{error}</Error>
  }

  if (loading || submitLoading) {
    return <Loading/>
  }

  /*
  INFO + SCHEMA
   */

  const {
    nazev: contractName,
    schemaPodani,
    sifrovaciCertifikat,
    sifrovaciMetoda,
    idSifrovacihoCertifikatu,
    typNazev,
    preklady,
    infoText,
    upozorneniText,
    lhuta// TODO nejaka kontrola ?
  } = data || {}

  const {
    predmet: predmetInputSchema,
    text: textInputSchema,
    cisloJednaci: cisloJednaciInputSchema,
    kriteria: kriteriaSchema,
    prilohy: prilohySchema,
    pocetPriloh: pocetPilohSchema,
    velikostPriloh: velikostPrilohSchema,
  } = schemaPodani?.properties || {}

  const prilohyItemsSchema = prilohySchema?.items?.properties ?? {}

  const critList = kriteriaSchema?.properties ?
    Object.entries(kriteriaSchema?.properties)
      .map(([name, { type }]) => ({
        name: name,
        type: type,
        label: preklady?.[`kriteria|${name}|nazev`],
        weight: preklady?.[`kriteria|${name}|vaha`],
        unit: preklady?.[`kriteria|${name}|jednotka`],
      })) : undefined

  const requiredPredmet = schemaPodani?.required?.includes(SCHEMA_KEYS.PREDMET)
  const requiredText = schemaPodani?.required?.includes(SCHEMA_KEYS.TEXT)
  const requiredCislojednaci = schemaPodani?.required?.includes(SCHEMA_KEYS.CISLO_JEDNACI)
  const requiredKriteria = schemaPodani?.required?.includes(SCHEMA_KEYS.KRITERIA)
  const requiredPrilohy = schemaPodani?.required?.includes(SCHEMA_KEYS.PRILOHY)

  const labelMapCrit: { [key: string]: string } = {}

  critList?.forEach((crit) => {
    labelMapCrit[crit.name] = crit.label || crit.name
  })

  /*
  PRILOHY
 */

  const formDataAttachFull: iSubmitAttach[] =
    filesUploaded.map(file => ({
      tmpId: file.response!.tmpId,// --pro připojení dříve uploadovaných příloh k podání
      nazev: file.fileData.name,// --filename originálu souboru
      velikostOriginalu: file.fileData.size,// --velikost v bytech originálu souboru
      pripona: file.fileData.name.split('.').pop() || '',// --přípona originálu souboru
      mimeTyp: file.fileData.type,// --mimeTyp originálu souboru
      hash: file.response!.hash //--SHA256 hash (zašifrovaného) souboru
    }))

  const formDataAttachBySchema: iSubmitAttach[] =
    formDataAttachFull.map(file =>
      Object.keys(prilohyItemsSchema).reduce((prev, cur) => ({
        ...prev, [cur]: file[cur as keyof typeof file]
      }), {})
    )

  /*
  FORM DATA
   */

  const formDataToSend: iSubmitFormData = { ...formData }
  if (pocetPilohSchema) {
    formDataToSend.pocetPriloh = formDataAttachFull.length
  }
  if (velikostPrilohSchema) {
    formDataToSend.velikostPriloh = formDataAttachFull.reduce((prev, file) => prev + (file?.velikostOriginalu ?? 0), 0)
  }
  if (prilohySchema) {
    formDataToSend.prilohy = formDataAttachBySchema
  }
  if (kriteriaSchema) {
    formDataToSend.kriteria = formDataCrits
  }

  /*
  VALIDACE
   */
  const validateRes = validate(formDataToSend, schemaPodani || {})

  const errMap = validateRes.errors.map(err => proccesValidErr(err, labelMapCrit))
  const errsPredmet = trySend || formData.predmet !== undefined ?
    errMap.filter(err => err.path === SCHEMA_KEYS.PREDMET)
    : []
  const errsText = trySend || formData.text !== undefined ?
    errMap.filter(err => err.path === SCHEMA_KEYS.TEXT)
    : []
  const errsCisloJednaci = trySend || formData.cisloJednaci !== undefined ?
    errMap.filter(err => err.path === SCHEMA_KEYS.CISLO_JEDNACI)
    : []
  const errsPrilohy = trySend ?
    errMap.filter(err => err.path.startsWith(SCHEMA_KEYS.PRILOHY))
    : []
  const errsKriteria = errMap.filter(err => err.path.startsWith(SCHEMA_KEYS.KRITERIA))

  const errsKriteriaInput = (inputName: string) => trySend || formDataCrits[inputName] !== undefined ?
    errsKriteria.filter(err => err.path === SCHEMA_KEYS.KRITERIA + '.' + inputName)
    : []

  /*
  ODESLANI
   */
  const handleSend = (e: any) => {
    e.preventDefault()

    if (errMap.length) {
      toastError('Data ve formuláři nejsou validní')
      return
    }
    setOpenModalConfirm(true)
  }

  const clickSend = async () => {
    const submitData: iSubmitExtSys = {
      idNadrizene: extObjectId,
      idSouvisejiciZpravy: responseId,
      typPodani: typeCode,
      datumCasOdeslani: moment().subtract(10, 'second').toISOString(),
    }

    if (sifrovaciCertifikat) {
      try {
        const sifra: string = await cipherString(JSON.stringify(formDataToSend), sifrovaciCertifikat)
        submitData.sifrovani = {
          sifrovaciCertifikat: sifrovaciCertifikat,
          idSifrovacihoCertifikatu: idSifrovacihoCertifikatu,
          sifrovaciMetoda: sifrovaciMetoda!,
          zasifrovanaDataPodani: sifra
        }
      } catch (e) {
        toastError('Zprávu se nepodařilo zašifrovat')
        return
      }
    } else {
      submitData.dataPodani = formDataToSend
    }

    if (prilohySchema) {
      submitData.prilohy = formDataAttachFull
    }

    console.log('send data:', submitData)

    setSubmitLoading(true)
    IenApi.submitExtSys(extSystemId, submitData)
      .then(resData => {
        toastSuccess('Zpráva odeslána')
        setSubmitResponse(resData)
      })
      .catch(err => {
        const ERR_MSG: any = {
          400: 'Špatný požadavek (neexistující typPodani, sifrovaciCertifikat, idSifrovacihoCertifikatu, sifrovaciMetoda, chybějící vyžadovaná data)',
          401: 'Uživatel není přihlášen (OAuth2)',
          403: 'Uživatel nemá oprávnění pro daný typPodani a idNadrizene, nebo uživatel nemá oprávnění odpovědět na zprávu idSouvisejiciZpravy či taková neexistuje',
          404: 'Objekt idNadrizene nebyl nalezen',
        }
        const errMsg = err.response?.status && ERR_MSG[err.response.status]
        if (errMsg) {
          toastError(errMsg)
        }
        errorIenProcesToast(err)
      })
      .finally(() => {
        setSubmitLoading(false)
      })
  }

  if (submitResponse) {
    const submitResponseTitle = submitResponse.stavEkomunikace === 'doruceno-ve-lhute'
      ? 'Podání proběhlo úspěšně'
      : submitResponse.stavEkomunikace === 'doruceno-po-lhute'
        ? 'Podání bylo doručeno po uplynutí lhůty'
        : ''
    const submitResponseTime = moment(submitResponse.datumCasDoruceni).format('DD.MM.YYYY HH:mm:ss')

    const clickGeneratePdf = async () => {
      generatePdf(submitResponseTitle, submitResponseTime, contractName || '', window.location.origin + contractLink, submitResponse.infoText)
    }

    return (
      <Box className={classes.box}>
        <Alert severity="success">
          <AlertTitle>
            {submitResponseTitle}
          </AlertTitle>
          Datum a čas doručení: {submitResponseTime}
          <br/>
          <p>
            {submitResponse.infoText}
          </p>
          <p>
            Podání/zpráva se v eKomunikaci zobrazí v seznamu odeslaných zpráv/podání,
            jakmile externí systém zašle zpětnou
            notifikaci (na čas doručení to však nemá vliv).
          </p>

          <p>
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={clickGeneratePdf}
            >
              Stáhnout potvrzení o odeslání podání/zprávy
            </Button>
            <br/>
            <br/>

            <Button
              size="small"
              variant="outlined"
              component={Link}
              to={contractLink}
            >
              Přejít na zadávací postup
            </Button>
            <br/>
            <br/>

            <Button
              size="small"
              variant="outlined"
              component={Link}
              to={EKOM_LINKS.base}
            >
              Přejít na eKomunikaci
            </Button>
          </p>

        </Alert>
      </Box>
    )
  }

  return (
    <>
      {/*<DebugBox data={data}/>*/}

      <ModalConfirm
        title={`Opravdu chcete ${isSubms ? 'podání' : 'zprávu'} odeslat?`}
        open={openModalConfirm}
        setOpen={setOpenModalConfirm}
        onConfirm={clickSend}
      />

      <Box className={classes.box}>
        <div>
          <strong>Zadávací postup:</strong>{' '}
          <Link to={contractLink}>
            {contractName}
          </Link>
        </div>

        <form onSubmit={handleSend}>
          <h2 className={classes.mainTitle}>{typNazev}</h2>

          {infoText && <Alert severity="info">{infoText}</Alert>}
          {upozorneniText && <Alert severity="warning">{upozorneniText}</Alert>}

          {predmetInputSchema &&
            <Box py={1}>
              <TextField
                fullWidth
                label="Předmět"
                variant="outlined"
                color="primary"
                required={requiredPredmet}
                error={!!errsPredmet.length}
                helperText={errsPredmet.map(e => <div>{e.message}</div>)}
                value={formData.predmet ?? ''}
                onChange={e => setFormDataVal({ predmet: e.target.value || undefined })}
              />
            </Box>
          }

          {textInputSchema &&
            <Box py={1}>
              <TextField
                fullWidth
                label="Text"
                variant="outlined"
                color="primary"
                required={requiredText}
                multiline
                minRows={4}
                error={!!errsText.length}
                helperText={errsText.map(e => <div>{e.message}</div>)}
                value={formData.text ?? ''}
                onChange={e => setFormDataVal({ text: e.target.value || undefined })}
              />
            </Box>
          }

          {cisloJednaciInputSchema &&
            <Box py={1}>
              <TextField
                fullWidth
                label="Čislo jednací"
                variant="outlined"
                color="primary"
                required={requiredCislojednaci}
                error={!!errsCisloJednaci.length}
                helperText={errsCisloJednaci.map(e => <div>{e.message}</div>)}
                value={formData.cisloJednaci ?? ''}
                onChange={e => setFormDataVal({ cisloJednaci: e.target.value || undefined })}
              />
            </Box>
          }

          {critList &&
            <Box py={1}>
              <h3 className={classes.title}>Hodnoty kritérií {requiredKriteria && '*'}</h3>

              <table className={classes.table}>
                <tbody>

                <tr>
                  <th>Název</th>
                  <th>Váha</th>
                  <th>Hodnota</th>
                  <th style={{ maxWidth: 160 }}>Jednotka</th>
                </tr>

                {critList.map(({ name, label, weight, unit, type }) =>
                  <tr key={name}>
                    <td className={classes.tableCell}>{label} {kriteriaSchema?.required.includes(name) && '*'}</td>
                    <td className={classes.tableCell}>{weight}</td>
                    <td className={classes.tableCell}>
                      {['number', 'integer'].includes(type) ?
                        <NumberFormat
                          style={{ width: 250 }}
                          name={name}
                          variant="outlined"
                          value={formDataCrits[name]}
                          defaultValue={undefined}
                          thousandSeparator={' '}
                          decimalSeparator={','}
                          decimalScale={type === 'integer' ? 0 : 8} // max 8 desetiniych mist
                          customInput={TextField}
                          isAllowed={(values) => {
                            const { value, floatValue, formattedValue } = values
                            if (floatValue && floatValue >= 10 ** 12) { // max 12 ccelych cisel
                              return false
                            }
                            if (value && value.length > 18) { // max 17 cifer + tecka
                              return false
                            }
                            return true
                          }}
                          onValueChange={(values) => {
                            let val: any = values.value
                            val = type === 'integer' ? parseInt(val) : parseFloat(val)
                            if (isNaN(val)) {
                              val = undefined
                            }
                            setFormDataCrits(data => ({ ...data, [name]: val }))
                          }}
                          isNumericString
                          required={kriteriaSchema?.required.includes(name)}
                          error={!!errsKriteriaInput(name).length}
                          helperText={errsKriteriaInput(name).map(e => <div>{e.message}</div>)}
                        />
                        :
                        <TextField
                          style={{ width: 250 }}
                          name={name}
                          variant="outlined"
                          color="primary"
                          value={formDataCrits[name] ?? ''}
                          required={kriteriaSchema?.required.includes(name)}
                          error={!!errsKriteriaInput(name).length}
                          helperText={errsKriteriaInput(name).map(e => <div>{e.message}</div>)}
                          onChange={e => {
                            let val: any = e.target.value
                            setFormDataCrits(data => ({ ...data, [name]: val || undefined }))
                          }}
                        />
                      }
                    </td>
                    <td className={classes.tableCell}>{unit}</td>
                  </tr>
                )}
                </tbody>

              </table>
            </Box>
          }

          {prilohySchema &&
            <Box py={1}>
              <h3 className={classes.title}>Přílohy {requiredPrilohy && '*'}</h3>
              {!!errsPrilohy.length && <Error>{errsPrilohy.map(e => <div>{e.message}</div>)}</Error>}
              <UploadBox name="prilohy" dataSchema={schemaPodani!}/>
            </Box>
          }

          {trySend && !!validateRes.errors.length &&
            <Box mt={5} py={1}>
              <Error
                title="Chyby ve formuláři">
                {errMap.map(err => <div>{err.message}</div>)}
              </Error>
            </Box>
          }

          <Box mt={5} py={1}>
            <Button
              type="submit"
              startIcon={<SendIcon/>}
              size="large" color="primary" variant="contained"
              onClick={() => {
                setTrySend(true)
              }}
              disabled={isUploading}
            >
              Odeslat
            </Button>
          </Box>

          {/*<DebugBox data={{ formDataToSend, errMap, errors: validateRes.errors }}/>*/}
        </form>
      </Box>
    </>
  )
}

export default withFormUpload(NewMsg)
